<script>
const OPAQUE_BLUE = "rgba(13, 185, 191, 0.5)";
const BLUE = "rgba(13, 185, 191, 0.8)";
const RED = "rgba(255, 23, 68, 0.8)";
const GREY = "rgba(124, 124, 124, 0.5)";

import { FullCalendar } from "vue-full-calendar";
import "fullcalendar/dist/fullcalendar.min.css";

import { mapState, mapActions } from "vuex";
import Modal from "@/components/_Shared/Modal";
import dateMixin from "@/mixins/dateMixin";
import tzMixin from "@/mixins/tzMixin";
import moment from "moment-timezone";

export default {
  mixins: [dateMixin, tzMixin],
  components: {
    Modal,
    FullCalendar
  },
  props: {
    professional: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      time_zone: null,
      events: [],
      orderSelected: null,
      eventSelected: null,
      showDeleteConfirmation: false,
      preloader: false,
      config: {
        timeZone: this.time_zone,
        contentHeight: "auto",
        defaultView: "agendaWeek",
        allDaySlot: false,
        eventOverlap: false,
        selectOverlap: false,
        editable: true,
        slotDuration: "01:00:00",
        selectConstraint: {
          start: "07:00",
          end: "22:00"
        },
        minTime: "07:00:00",
        maxTime: "22:00:00",
        slotLabelFormat: "HH",
        columnHeaderFormat: "ddd DD/MM",
        firstDay: moment().day(),
        nowIndicator: true,
        validRange: {
          start: moment().subtract(1, "days"),
          end: moment().add(4, "M")
        },
        header: {
          left: "",
          center: "",
          right: "today prev,next"
        },
        buttonText: {
          today: "hoje"
        },
        monthNames: [
          "Janeiro",
          "Fevereiro",
          "Março",
          "Abril",
          "Maio",
          "Junho",
          "Julho",
          "Agosto",
          "Setembro",
          "Outubro",
          "Novembro",
          "Dezembro"
        ],
        monthNamesShort: [
          "Jan",
          "Fev",
          "Mar",
          "Abr",
          "Mai",
          "Jun",
          "Jul",
          "Ago",
          "Set",
          "Out",
          "Nov",
          "Dez"
        ],
        dayNames: [
          "Domingo",
          "Segunda",
          "Terça",
          "Quarta",
          "Quinta",
          "Sexta",
          "Sábado"
        ],
        dayNamesShort: ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sab"]
      }
    };
  },
  watch: {
    professional(data) {
      this.setEventsAndTz(data);
    }
  },
  mounted() {
    this.setEventsAndTz(this.professional);
  },
  methods: {
    ...mapActions("professionals", [
      "createAvailableEvent",
      "updateAvailableEvent",
      "removeAvailableEvent"
    ]),
    ...mapActions("orders", ["getByEvent"]),

    changeColorEvent(event_id, color) {
      this.events.find(x => x.id === event_id).color = color;
    },

    setEventsAndTz(professional) {
      this.time_zone = this.tzValue(professional.account.time_zone);

      if (professional.account.schedule)
        professional.account.schedule.events.forEach(event => {
          this.verifyEvent(event);
        });
    },

    verifyEvent(event) {
      let title, color, editable, rendering;

      if (event.status === "available") {
        title = "disponível";
        color = BLUE;
        editable = true;
      } else if (event.status === "order") {
        title = "limpeza";
        color = "rgba(96, 50, 133, .85)";
        editable = false;
      } else if (event.status === "commute") {
        title = "deslocamento";
        color = GREY;
        editable = false;
      } else {
        rendering = "background";
      }

      this.events.push({
        id: event.id,
        newEvent: false,
        changed: false,
        title,
        color,
        rendering,
        editable,
        start: moment.tz(event.starts_at, this.time_zone).format(),
        end: moment.tz(event.ends_at, this.time_zone).format(),
        status: event.status,
        overlap: false,
        resourceEditable: false
      });
    },

    async updateEvent(event) {
      this.preloader = true;

      try {
        let data = {
          id: event.id,
          starts_at: event.start,
          ends_at: event.end,
          time_zone: this.professional.account.time_zone
        };

        let res = await this.updateAvailableEvent(data);

        // returns default transparency
        if (event.status == "available") {
          this.changeColorEvent(event.id, BLUE);
        } else {
          this.changeColorEvent(event.id, GREY);
        }
      } catch (e) {
        this.changeColorEvent(event.id, RED);
        this.$toast.error(e);
      }

      this.preloader = false;
    },

    async storeEvent(event) {
      this.preloader = true;

      try {
        let data = {
          id: event.id,
          schedule_id: this.professional.account.schedule.id,
          starts_at: event.start,
          ends_at: event.end,
          time_zone: this.professional.account.time_zone
        };

        let res = await this.createAvailableEvent(data);
        this.removeFromEvents(data.id);
        this.verifyEvent(res);
      } catch (e) {
        this.changeColorEvent(event.id, RED);
        this.$toast.error(e);
      }

      this.preloader = false;
    },

    async removeEvent(event_id) {
      this.preloader = true;

      try {
        let res = await this.removeAvailableEvent(event_id);
      } catch (e) {
        this.$toast.error(e);
      }

      this.preloader = false;
    },

    async remove() {
      try {
        let newEvent = this.eventSelected.newEvent;
        let eventId = this.eventSelected.id;
        let overlappingEvents = this.eventSelected.overlappingEvents;

        if (!newEvent) await this.removeEvent(eventId);

        if (overlappingEvents && overlappingEvents.length)
          await overlappingEvents.forEach(async el => {
            await this.removeEvent(el.id);
          });

        this.removeFromEvents(eventId);
        this.showDeleteConfirmation = false;
      } catch (e) {
        this.$toast.error(e);
      }
    },

    save() {
      this.events.forEach(async event => {
        if (event.newEvent) await this.store(event);
        else if (event.changed) await this.updateEvent(event);
      });
    },

    store(event) {
      event.overlappingEvents && event.overlappingEvents.length
        ? this.deleteMultipleEvents(event)
        : this.storeEvent(event);
    },

    deleteMultipleEvents(event) {
      let events = event.overlappingEvents;
      let num = events.length;
      let deleted = 0;

      events.forEach(async el => {
        try {
          let res = await this.removeAvailableEvent(el.id);
          if (res) deleted++;
          if (num === deleted) this.storeEvent(event);
        } catch (e) {
          this.$toast.error(e);
        }
      });
    },

    removeFromEvents(id) {
      this.events = this.events.filter(event => event.id !== id);
    },

    groupCheck(event) {
      let before = this.events.find(
        el =>
          moment(el.end).toISOString() == moment(event.start).toISOString() &&
          el.status == "available"
      );
      let after = this.events.find(
        el =>
          moment(el.start).toISOString() == moment(event.end).toISOString() &&
          el.status == "available"
      );

      if (!event.overlappingEvents) event.overlappingEvents = [];
      if (before && event.status != "commute") {
        event.start = before.start;
        this.removeFromEvents(before.id);

        if (before.overlappingEvents)
          event.overlappingEvents = event.overlappingEvents.concat(
            before.overlappingEvents
          );

        if (!before.newEvent) event.overlappingEvents.push(before);
      }

      if (after && event.status != "commute") {
        event.end = after.end;
        this.removeFromEvents(after.id);

        if (after.overlappingEvents)
          event.overlappingEvents = event.overlappingEvents.concat(
            after.overlappingEvents
          );
        if (!after.newEvent) event.overlappingEvents.push(after);
      }

      return event;
    },

    created(event) {
      event.start = moment(event.start).toISOString();
      event.end = moment(event.end).toISOString();

      event = this.groupCheck(event);

      this.events.push({
        id: Math.random()
          .toString(36)
          .substr(2, 9),
        title: "disponível",
        color: OPAQUE_BLUE,
        start: event.start,
        end: event.end,
        newEvent: true,
        status: "available",
        overlappingEvents: event.overlappingEvents
          ? event.overlappingEvents
          : []
      });
    },

    droppedOrResized(e) {
      let start = moment(e.start);
      let end = moment(e.end);

      let event = this.events.find(x => x.id === e.id);
      event.start = start.toISOString();
      event.end = end.toISOString();
      event = this.groupCheck(event);

      event.color =
        start.format("H") < 7 || end.format("H") > 22
          ? RED
          : this.isavailable(event);

      if (!event.newEvent && event.hasOwnProperty("changed"))
        event.changed = true;
    },

    isavailable(e) {
      if (e.status === "available") {
        return OPAQUE_BLUE;
      } else {
        return GREY;
      }
    },

    selected(e) {
      if (e.status === "order") {
        this.setOrderSelected(e.id);
      } else if (e.status === "available" || e.status === "commute") {
        this.eventSelected = e;
        this.showDeleteConfirmation = true;
      }
    },

    onDrop(e, delta, revert) {
      if (e.status === "commute") return revert();
      this.droppedOrResized;
    },

    async setOrderSelected(eventID) {
      try {
        this.orderSelected = await this.getByEvent(eventID);
      } catch (e) {
        this.$toast.error("Ocorreu um erro ao buscar o pedido!");
      }
    }
  }
};
</script>

<template>
  <div>
    <full-calendar
      ref="calendar"
      @event-created="created"
      @event-resize="droppedOrResized"
      @event-drop="onDrop"
      @event-selected="selected"
      :config="config"
      :events="events"
    />

    <div class="btn-group mt-20">
      <button
        class="btn bg-purple"
        :class="{ 'btn--loading': preloader }"
        @click="save"
      >
        <span>salvar alterações</span>
      </button>
    </div>

    <Modal
      title="VOCÊ DESEJA DELETAR O EVENTO?"
      subtitle="Esta ação não poderá ser desfeita"
      size="sm"
      @close="showDeleteConfirmation = false"
      v-if="showDeleteConfirmation"
    >
      <template slot="body">
        <div>
          <b>Dia:</b>
          {{ eventSelected.start | formatDate("DD/MM/Y") }}
          <br />
          <b>Início:</b>
          {{ eventSelected.start | formatDate("HH") }} hrs
          <br />
          <b>Fim:</b>
          {{ eventSelected.end | formatDate("HH") }} hrs
        </div>
      </template>

      <template slot="footer">
        <button
          class="btn bg-purple-outline"
          @click="showDeleteConfirmation = false"
        >
          fechar
        </button>
        <button
          class="btn bg-purple"
          :class="{ 'btn--loading': preloader }"
          @click="remove"
        >
          <span>Deletar</span>
        </button>
      </template>
    </Modal>

    <Modal
      title="DETALHES DO PEDIDO"
      subtitle="Abaixo estão os detalhes do pedido"
      size="sm"
      @close="orderSelected = null"
      v-if="orderSelected"
    >
      <template slot="body">
        <div>
          <b>Dia:</b>
          {{ orderSelected.schedule_event.starts_at | formatDate("DD/MM/Y") }}
          <br />
          <b>Início:</b>
          {{ orderSelected.schedule_event.starts_at | formatDate("HH") }} hrs
          <br />
          <b>Fim:</b>
          {{ orderSelected.schedule_event.ends_at | formatDate("HH") }} hrs
        </div>
      </template>

      <template slot="footer">
        <button class="btn bg-purple-outline" @click="orderSelected = null">
          fechar
        </button>
        <router-link
          class="btn bg-purple"
          :to="{ name: 'order', params: { id: orderSelected.id } }"
          >Ir para o pedido</router-link
        >
      </template>
    </Modal>
  </div>
</template>

<style scoped></style>



// WEBPACK FOOTER //
// src/components/Professionals/Edit/Tabs/Schedule.vue